import { useEffect, useRef, useState } from "react";
import { useQuery, useSubscription } from "@apollo/client";
import { WAVE_EVENTS_SUB, RECENT_WAVE_EVENTS } from "../queries/waves";
import { formatDateSGT, formatTimeSGT } from "./dateFormat";

const MAX_EVENTS = 200;
const CACHE_VERSION = "v2";
const storageKey = (waveId) => `wave_events_${CACHE_VERSION}_${waveId}`;

function loadCached(waveId) {
    try {
        return JSON.parse(sessionStorage.getItem(storageKey(waveId)) || "[]");
    } catch { return []; }
}

function saveCache(waveId, events) {
    try {
        sessionStorage.setItem(storageKey(waveId), JSON.stringify(events.slice(0, MAX_EVENTS)));
    } catch {}
}

export default function WaveMonitorPage({ waveId }) {
    const [events, setEvents] = useState(() => loadCached(waveId));
    const seededRef = useRef(false);
    const { data: historyData } = useQuery(RECENT_WAVE_EVENTS, { variables: { waveId, limit: MAX_EVENTS } });
    const { data, error } = useSubscription(WAVE_EVENTS_SUB, { variables: { waveId } });

    // Reset seed flag on mount/waveId change so each mount gets fresh DB data
    useEffect(() => {
        seededRef.current = false;
    }, [waveId]);

    // Seed from DB exactly once per mount — never overwrite subscription events
    useEffect(() => {
        if (!historyData?.recentWaveEvents || seededRef.current) return;
        seededRef.current = true;
        const fromDb = historyData.recentWaveEvents;
        saveCache(waveId, fromDb);
        setEvents(fromDb);
    }, [historyData, waveId]);

    // Append live subscription events
    useEffect(() => {
        if (!data?.waveEvents) return;
        setEvents(prev => {
            const next = [data.waveEvents, ...prev].slice(0, MAX_EVENTS);
            saveCache(waveId, next);
            return next;
        });
    }, [data, waveId]);

    return (
        <div className="admin-section">
            <h3>Live event stream</h3>
            {error && <p style={{color:"crimson"}}>Subscription error: {error.message}</p>}
            <div style={{ maxHeight: 480, overflowY: "auto" }}>
                <table className="admin-table">
                    <thead>
                        <tr><th>Type</th><th>Date</th><th>Time</th><th>Payload</th></tr>
                    </thead>
                    <tbody>
                        {events.length === 0 && (
                            <tr><td colSpan={4} style={{ color: "#888", textAlign: "center" }}>Waiting for events.</td></tr>
                        )}
                        {events.map((e, idx) => (
                            <tr key={idx}>
                                <td>{e.type}</td>
                                <td>{e.occurredAt ? formatDateSGT(e.occurredAt) : "-"}</td>
                                <td>{e.occurredAt ? formatTimeSGT(e.occurredAt) : "-"}</td>
                                <td style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>{e.payload || "-"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
